import { useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Clock, Check, Sparkles, Plus } from "lucide-react";
import { bookUrl } from "../data/siteConfig";

export const ServiceDrawer = ({ service, onClose }) => {
  useEffect(() => {
    if (!service) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [service, onClose]);

  return (
    <AnimatePresence>
      {service && (
        <motion.div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          aria-label={`${service.name} details`}
          data-testid="service-drawer"
        >
          <motion.div
            className="absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-3xl border-t border-hotpink/30 bg-coal md:inset-y-0 md:left-auto md:right-0 md:w-[480px] md:rounded-none md:border-l md:border-t-0"
            initial={{ y: "8%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "8%", opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={service.image}
                alt={`${service.name} example`}
                className="h-60 w-full object-cover"
              />
              <button
                onClick={onClose}
                aria-label="Close details"
                data-testid="service-drawer-close"
                className="absolute right-4 top-4 rounded-full bg-black/70 p-2 transition-colors duration-300 hover:bg-hotpink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-7">
              <p className="text-[10px] uppercase tracking-[0.3em] text-hotpink">
                {service.category}
              </p>
              <h3 className="mt-1 font-serif text-3xl">{service.name}</h3>

              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full border border-white/15 px-3 py-1.5 text-white/80">
                  Starting at <strong className="text-hotpink">${service.price}</strong>
                </span>
                <span className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-white/80">
                  <Clock className="h-3 w-3 text-hotpink" /> {service.duration}
                </span>
                <span className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-white/80">
                  <Sparkles className="h-3 w-3 text-hotpink" /> {service.hairNote}
                </span>
              </div>

              <p className="mt-5 text-sm leading-relaxed text-white/60">
                {service.description}
              </p>

              <h4 className="mt-7 font-serif text-lg">How to prep</h4>
              <ul className="mt-3 space-y-2 text-sm text-white/60">
                {service.prep.map((p) => (
                  <li key={p} className="flex gap-2.5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-hotpink" />
                    {p}
                  </li>
                ))}
              </ul>

              <h4 className="mt-7 font-serif text-lg">Optional add-ons</h4>
              <ul className="mt-3 space-y-2 text-sm text-white/60">
                {service.addons.map((a) => (
                  <li key={a.name} className="flex items-center justify-between gap-3">
                    <span className="flex gap-2.5">
                      <Plus className="mt-0.5 h-4 w-4 shrink-0 text-hotpink" />
                      {a.name}
                    </span>
                    <span className="text-white/80">+${a.price}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={bookUrl(service.id)}
                data-testid="service-drawer-book"
                className="mt-8 block rounded-full bg-hotpink py-4 text-center text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-magenta"
              >
                Book This Style
              </Link>
              <p className="mt-3 text-center text-[11px] text-white/35">
                Prototype pricing — final pricing subject to owner approval.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

